import { useCallback, useEffect, useState } from 'react'
import { elementTypes } from '@scene'
import { last } from 'lodash-es'
import { Object3D } from 'three'
import { useDebounce } from 'use-debounce'
import { coordinateEventName } from '@ui/events'
import {
  createInteractableByType,
  useElementSelectionStore,
  useModelStore,
  useControlStore,
} from '@editorStores'
import useMarqueeSelectionListener from './useMarqueeSelectionListener'
import useModelClickListeners from './useModelClickListeners'

interface Arguments {
  highlightedIds?: Set<string>
  allowSelectHighlighted?: boolean
}

const useElementSelection = ({ allowSelectHighlighted = false }: Arguments = {}) => {
  useMarqueeSelectionListener((ids, objects) => {
    setSelectedObjectsLocal(objects)
  })

  const isMarqueeSelectionActive = useControlStore(state => state.isMarqueeSelection)
  const [selectedObjects, setSelectedObjectsLocal] = useState<Object3D[]>([])
  const clearSelectedObjects = useCallback(() => {
    setSelectedObjectsLocal([])
  }, [])
  const [debouncedSelectedObjects] = useDebounce(selectedObjects, 10)

  const getModelStoreState = useModelStore.getState
  const selectionModeKey = useControlStore(state => state.selectionModeKey)

  const {
    setSelectedStandaloneIds,
    setSelectionEnabled,
    selectedStandAloneIds,
    addSelectedStandaloneIds,
    deselectStandAloneId,
    deselectAllIds,
    highlightedIds,
    resetHighlightedIds,
  } = useElementSelectionStore()

  // Check if coordinate input mode is active
  const isCoordinateInputMode = selectionModeKey?.startsWith(coordinateEventName)

  useModelClickListeners(
    event => {
      const {
        name: id,
        userData: { elementType },
      } = event.object
      const interactableByType =
        last(getModelStoreState().interactableByTypeConfigs)?.interactableByType ||
        createInteractableByType(elementTypes)

      const isTypeInteractable = interactableByType[elementType as ElementTypes]

      if (!isTypeInteractable) return

      const isHighlighted = highlightedIds?.has(id)

      if (isHighlighted && !allowSelectHighlighted) return

      if (selectedStandAloneIds.has(id)) deselectStandAloneId(id)
      else addSelectedStandaloneIds([id])
    },
    [selectedStandAloneIds, highlightedIds],
    !isCoordinateInputMode, // Disable click listeners when coordinate input mode is active
  )

  useEffect(() => {
    if (!isMarqueeSelectionActive || !debouncedSelectedObjects) {
      clearSelectedObjects()
      return
    }
    const interactableByType =
      last(getModelStoreState().interactableByTypeConfigs)?.interactableByType ||
      createInteractableByType(elementTypes)

    // using loop for performance
    const ids = new Array(debouncedSelectedObjects.length)
    for (let i = 0; i < debouncedSelectedObjects.length; ++i) {
      const id = debouncedSelectedObjects[i].name
      const { elementType } = debouncedSelectedObjects[i].userData
      const isTypeInteractable = interactableByType[elementType as ElementTypes]

      if (!id || !isTypeInteractable) continue

      const isHighlighted = highlightedIds?.has(id)

      if (!isHighlighted || (isHighlighted && allowSelectHighlighted)) {
        ids[i] = debouncedSelectedObjects[i].name
      }
    }

    setSelectedStandaloneIds(ids.filter(Boolean))
  }, [isMarqueeSelectionActive, debouncedSelectedObjects])

  useEffect(() => {
    // Disable element selection when coordinate input mode is active
    setSelectionEnabled(!isCoordinateInputMode)

    return () => {
      setSelectionEnabled(false)
      deselectAllIds()
      resetHighlightedIds()
    }
  }, [isCoordinateInputMode])
}

export default useElementSelection
